// Type definitions for the local-first BSB Bible data layer (M.3.6.3).
//
// Mirrors the JSON output of `scripts/convert-bsb-to-json.mjs` verbatim:
// - `public/bible/bsb/manifest.json` -> BsbManifest
// - `public/bible/bsb/{slug}.json`   -> BsbBook
//
// The converter script is the single source of truth for these shapes.
// If the converter changes the on-disk shape, update both sides together
// or hydration validation in `bsb-bootstrap.ts` will reject the manifest
// or per-book payload.
//
// `BsbMeta` is the IDB-only marker row written after a successful
// hydration; it never travels over the wire.

export interface TextSegment {
  text: string;
  kind?: 'wj';
}

export interface BsbVerse {
  v: number;
  vCont?: true;
  omitted?: true;
  t: string | TextSegment[];
}

export type ParagraphMarker = 'p' | 'pmo' | 'pc' | 'li1' | 'li2' | 'd';
export type PoetryIndent = 1 | 2 | 'right';

export interface PoetryLine {
  indent: PoetryIndent;
  verses: BsbVerse[];
}

export interface ParagraphBlock {
  type: 'paragraph';
  marker: ParagraphMarker;
  verses: BsbVerse[];
}

export interface PoetryBlock {
  type: 'poetry';
  lines: PoetryLine[];
}

export interface AcrosticBlock {
  type: 'acrostic';
  text: string;
}

export interface TitleBlock {
  type: 'title';
  text: string;
}

export interface SectionBlock {
  type: 'section';
  level: 1 | 2;
  text: string;
}

export interface MajorSectionBlock {
  type: 'majorSection';
  text: string;
}

export interface ReferenceBlock {
  type: 'reference';
  text: string;
  locs: string[];
}

export interface BlankBlock {
  type: 'blank';
}

export type BsbBlock =
  | ParagraphBlock
  | PoetryBlock
  | AcrosticBlock
  | TitleBlock
  | SectionBlock
  | MajorSectionBlock
  | ReferenceBlock
  | BlankBlock;

export interface BsbBook {
  book: string;
  slug: string;
  order: number;
  translation: 'BSB';
  chapters: Record<string, BsbBlock[]>;
}

export interface BsbMeta {
  key: 'manifest';
  sourceSha256: string;
  hydratedAt: number;
  sourceEdition: string;
}

// Resumable first-time hydration checkpoint (2026-08-25). Lives as a
// SECOND row in the bsb-meta store (key 'inventory') and records which
// books have been fetched + validated + written under `sha` so an
// interrupted or partially-failed first hydration RESUMES from where it
// stopped instead of restarting all 66 books from zero. The row is
// deleted the moment the full corpus verifies and the BsbMeta row is
// written — BsbMeta remains the sole "complete Bible is on this
// device" signal. A sha change mid-resume resets the checkpoint (books
// are refetched under the new sha; same-slug puts overwrite).
export interface BsbInventory {
  key: 'inventory';
  sha: string;
  savedSlugs: string[];
  updatedAt: number;
}

export interface BsbManifestBookEntry {
  slug: string;
  name: string;
  order: number;
  chapters: number;
  verses: number;
  sourceSha256: string;
}

export interface BsbManifest {
  translation: 'BSB';
  translationFull: string;
  license: string;
  source: string;
  downloadsUrl: string;
  licenseUrl: string;
  termsUrl: string;
  sourceEdition: string;
  sourceFormat: string;
  outputFormat: string;
  sourceSha256: string;
  totalVerses: number;
  books: BsbManifestBookEntry[];
}

// ----------------------------------------------------------------
// Canonical 66-book registry (M.3.6.3.c): client-side source of truth.
// ----------------------------------------------------------------
//
// This list is duplicated across three locations and must stay in
// lock-step with all of them:
//   1. scripts/convert-bsb-to-json.mjs (CANONICAL_BOOKS)
//      Build-time source of truth for the on-disk per-book JSONs and
//      the on-disk manifest.
//   2. src/lib/bsb-types.ts (this file, CANONICAL_BOOKS below)
//      Client-side runtime canonical for the parser and the bootstrap
//      module.
//   3. public/bible/bsb/manifest.json (generated artifact)
//      Output of the converter; the bootstrap validator cross-checks
//      this against (2) at hydrate time.
//
// Per CLAUDE.md core rule #4 cross-boundary-duplication sub-rule,
// each side owns its own copy. Adding or removing a book is a
// manual three-file edit.

export interface CanonicalBookEntry {
  readonly slug: string;
  readonly name: string;
  readonly order: number;
  readonly chapters: number;
  // Optional override of the display name in book-title contexts where
  // the canonical `name` differs from how the book should be titled.
  // Verse-reference contexts (chapter h1, Continue Reading card) continue
  // to use `name`. When omitted, consumers should fall back to `name`.
  readonly bookTitle?: string;
}

export const CANONICAL_BOOKS: ReadonlyArray<CanonicalBookEntry> = [
  { slug: 'genesis', name: 'Genesis', order: 1, chapters: 50 },
  { slug: 'exodus', name: 'Exodus', order: 2, chapters: 40 },
  { slug: 'leviticus', name: 'Leviticus', order: 3, chapters: 27 },
  { slug: 'numbers', name: 'Numbers', order: 4, chapters: 36 },
  { slug: 'deuteronomy', name: 'Deuteronomy', order: 5, chapters: 34 },
  { slug: 'joshua', name: 'Joshua', order: 6, chapters: 24 },
  { slug: 'judges', name: 'Judges', order: 7, chapters: 21 },
  { slug: 'ruth', name: 'Ruth', order: 8, chapters: 4 },
  { slug: '1-samuel', name: '1 Samuel', order: 9, chapters: 31 },
  { slug: '2-samuel', name: '2 Samuel', order: 10, chapters: 24 },
  { slug: '1-kings', name: '1 Kings', order: 11, chapters: 22 },
  { slug: '2-kings', name: '2 Kings', order: 12, chapters: 25 },
  { slug: '1-chronicles', name: '1 Chronicles', order: 13, chapters: 29 },
  { slug: '2-chronicles', name: '2 Chronicles', order: 14, chapters: 36 },
  { slug: 'ezra', name: 'Ezra', order: 15, chapters: 10 },
  { slug: 'nehemiah', name: 'Nehemiah', order: 16, chapters: 13 },
  { slug: 'esther', name: 'Esther', order: 17, chapters: 10 },
  { slug: 'job', name: 'Job', order: 18, chapters: 42 },
  { slug: 'psalm', name: 'Psalm', bookTitle: 'Psalms', order: 19, chapters: 150 },
  { slug: 'proverbs', name: 'Proverbs', order: 20, chapters: 31 },
  { slug: 'ecclesiastes', name: 'Ecclesiastes', order: 21, chapters: 12 },
  { slug: 'song-of-solomon', name: 'Song of Solomon', order: 22, chapters: 8 },
  { slug: 'isaiah', name: 'Isaiah', order: 23, chapters: 66 },
  { slug: 'jeremiah', name: 'Jeremiah', order: 24, chapters: 52 },
  { slug: 'lamentations', name: 'Lamentations', order: 25, chapters: 5 },
  { slug: 'ezekiel', name: 'Ezekiel', order: 26, chapters: 48 },
  { slug: 'daniel', name: 'Daniel', order: 27, chapters: 12 },
  { slug: 'hosea', name: 'Hosea', order: 28, chapters: 14 },
  { slug: 'joel', name: 'Joel', order: 29, chapters: 3 },
  { slug: 'amos', name: 'Amos', order: 30, chapters: 9 },
  { slug: 'obadiah', name: 'Obadiah', order: 31, chapters: 1 },
  { slug: 'jonah', name: 'Jonah', order: 32, chapters: 4 },
  { slug: 'micah', name: 'Micah', order: 33, chapters: 7 },
  { slug: 'nahum', name: 'Nahum', order: 34, chapters: 3 },
  { slug: 'habakkuk', name: 'Habakkuk', order: 35, chapters: 3 },
  { slug: 'zephaniah', name: 'Zephaniah', order: 36, chapters: 3 },
  { slug: 'haggai', name: 'Haggai', order: 37, chapters: 2 },
  { slug: 'zechariah', name: 'Zechariah', order: 38, chapters: 14 },
  { slug: 'malachi', name: 'Malachi', order: 39, chapters: 4 },
  { slug: 'matthew', name: 'Matthew', order: 40, chapters: 28 },
  { slug: 'mark', name: 'Mark', order: 41, chapters: 16 },
  { slug: 'luke', name: 'Luke', order: 42, chapters: 24 },
  { slug: 'john', name: 'John', order: 43, chapters: 21 },
  { slug: 'acts', name: 'Acts', order: 44, chapters: 28 },
  { slug: 'romans', name: 'Romans', order: 45, chapters: 16 },
  { slug: '1-corinthians', name: '1 Corinthians', order: 46, chapters: 16 },
  { slug: '2-corinthians', name: '2 Corinthians', order: 47, chapters: 13 },
  { slug: 'galatians', name: 'Galatians', order: 48, chapters: 6 },
  { slug: 'ephesians', name: 'Ephesians', order: 49, chapters: 6 },
  { slug: 'philippians', name: 'Philippians', order: 50, chapters: 4 },
  { slug: 'colossians', name: 'Colossians', order: 51, chapters: 4 },
  { slug: '1-thessalonians', name: '1 Thessalonians', order: 52, chapters: 5 },
  { slug: '2-thessalonians', name: '2 Thessalonians', order: 53, chapters: 3 },
  { slug: '1-timothy', name: '1 Timothy', order: 54, chapters: 6 },
  { slug: '2-timothy', name: '2 Timothy', order: 55, chapters: 4 },
  { slug: 'titus', name: 'Titus', order: 56, chapters: 3 },
  { slug: 'philemon', name: 'Philemon', order: 57, chapters: 1 },
  { slug: 'hebrews', name: 'Hebrews', order: 58, chapters: 13 },
  { slug: 'james', name: 'James', order: 59, chapters: 5 },
  { slug: '1-peter', name: '1 Peter', order: 60, chapters: 5 },
  { slug: '2-peter', name: '2 Peter', order: 61, chapters: 3 },
  { slug: '1-john', name: '1 John', order: 62, chapters: 5 },
  { slug: '2-john', name: '2 John', order: 63, chapters: 1 },
  { slug: '3-john', name: '3 John', order: 64, chapters: 1 },
  { slug: 'jude', name: 'Jude', order: 65, chapters: 1 },
  { slug: 'revelation', name: 'Revelation', order: 66, chapters: 22 },
];

export const CANONICAL_SLUG_TO_NAME: ReadonlyMap<string, string> = new Map(
  CANONICAL_BOOKS.map((b) => [b.slug, b.name] as const),
);

// Mirrors CANONICAL_SLUG_TO_NAME but returns the book-title form. Used by
// title-context surfaces (the book index, the BibleContextBlock header
// label) where Psalms wants the plural form. Falls back to `name` for
// every entry without an explicit bookTitle override.
export const CANONICAL_SLUG_TO_BOOK_TITLE: ReadonlyMap<string, string> = new Map(
  CANONICAL_BOOKS.map((b) => [b.slug, b.bookTitle ?? b.name] as const),
);

export const CANONICAL_SLUG_TO_CHAPTERS: ReadonlyMap<string, number> = new Map(
  CANONICAL_BOOKS.map((b) => [b.slug, b.chapters] as const),
);

// ----------------------------------------------------------------
// OT/NT classifiers (M.3.8.a).
// ----------------------------------------------------------------
//
// Derived from the canonical `order` field; no per-book testament tag
// is added to CanonicalBookEntry. Order 1..39 is the Protestant Old
// Testament; 40..66 is the New Testament. Both helpers throw on
// invalid input so a typo or out-of-range value fails loudly at the
// call site rather than silently mis-classifying.

export function testamentOf(order: number): 'OT' | 'NT' {
  if (!Number.isInteger(order) || order < 1 || order > 66) {
    throw new Error(`testamentOf: order out of range 1..66, got ${order}`);
  }
  return order <= 39 ? 'OT' : 'NT';
}

export function isOldTestament(slug: string): boolean {
  const entry = CANONICAL_BOOKS.find((b) => b.slug === slug);
  if (!entry) throw new Error(`isOldTestament: unknown slug ${slug}`);
  return testamentOf(entry.order) === 'OT';
}

// ----------------------------------------------------------------
// Chapter-to-chapter neighbor helpers (M.3.8.c).
// ----------------------------------------------------------------
//
// Pure functions over CANONICAL_BOOKS. Both helpers handle three
// guard cases up front (unknown slug, non-integer chapter,
// out-of-range chapter) and return null. Within-book transitions
// stay on the same book; cross-book transitions step to the
// adjacent book at its first or last chapter. Genesis 1 prev and
// Revelation 22 next return null (canon edges).

export interface ChapterNeighbor {
  bookSlug: string;
  bookName: string;
  chapter: number;
}

export function getPrevChapter(
  slug: string,
  chapter: number,
): ChapterNeighbor | null {
  if (!Number.isInteger(chapter)) return null;
  const book = CANONICAL_BOOKS.find((b) => b.slug === slug);
  if (!book) return null;
  if (chapter < 1 || chapter > book.chapters) return null;
  if (chapter > 1) {
    return { bookSlug: slug, bookName: book.name, chapter: chapter - 1 };
  }
  if (book.order === 1) return null;
  const prevBook = CANONICAL_BOOKS.find((b) => b.order === book.order - 1);
  if (!prevBook) return null;
  return {
    bookSlug: prevBook.slug,
    bookName: prevBook.name,
    chapter: prevBook.chapters,
  };
}

export function getNextChapter(
  slug: string,
  chapter: number,
): ChapterNeighbor | null {
  if (!Number.isInteger(chapter)) return null;
  const book = CANONICAL_BOOKS.find((b) => b.slug === slug);
  if (!book) return null;
  if (chapter < 1 || chapter > book.chapters) return null;
  if (chapter < book.chapters) {
    return { bookSlug: slug, bookName: book.name, chapter: chapter + 1 };
  }
  if (book.order === 66) return null;
  const nextBook = CANONICAL_BOOKS.find((b) => b.order === book.order + 1);
  if (!nextBook) return null;
  return { bookSlug: nextBook.slug, bookName: nextBook.name, chapter: 1 };
}
